/* Scroll «una sección por gesto» (D-5, corrige M-1 y M-5).

   Un tick de rueda, una tecla de página o un clic en el menú dejan la sección
   encajada exactamente bajo la cabecera, a la primera. Para eso el módulo es
   el único que manda:

   - No hay scroll-snap de CSS en paralelo, y durante la animación se apaga
     `scroll-behavior:smooth` para que el navegador no vuelva a suavizar cada
     paso del bucle (era una de las razones de que el salto se quedara corto).
   - La animación es propia (requestAnimationFrame + easing), así que sabemos
     con certeza cuándo termina. Antes se confiaba en `behavior:'smooth'`, que
     no avisa y obligaba a adivinar la duración con un temporizador.
   - El índice de sección activa se guarda; no se recalcula desde la posición
     en mitad de un desplazamiento, que era lo que hacía falta un segundo
     gesto para avanzar uno.
   - Las medidas se cachean y solo se rehacen al cambiar el tamaño, al cargar
     fuentes e imágenes o al recargar una sección desde el editor de temas.

   **En el móvil el motor no manda: ahí el scroll es libre.** Es una decisión,
   no una limitación (2026-09-05). Las secciones que en escritorio miden 724 de
   724 px, apiladas en un móvil miden entre 1000 y 1900: no caben en pantalla,
   y un carrusel de un gesto por sección solo funciona si cada pantalla se lee
   entera; aquí cortaría el texto a media frase. Además el gesto peleaba con lo
   que ya hace el navegador —el rebote, la inercia y la barra de direcciones
   que aparece y desaparece—, y de esa pelea salía el enganche. De todo el
   motor, en el móvil solo queda vivo el salto de los enlaces del menú, que es
   el que deja la sección justo bajo la cabecera.

   No intercepta las teclas con el foco en un campo de texto, ni la rueda
   encima de algo que se desplaza por dentro (un textarea, un desplegable), ni
   nada con Ctrl (zoom), con el menú móvil abierto o en pantallas táctiles o
   estrechas. Si una sección no cabe en la pantalla (zoom 200 %, letra grande),
   deja el scroll nativo hasta llegar a su borde.

   **El final de la página siempre se alcanza.** La última sección comparte la
   pantalla con el pie, y el CSS la calcula para que quepan las dos. Si aun así
   no caben —el pie con títulos largos, el euskera, una ventana muy baja—, hay
   una parada más: el final de la página, con el pie entero. Antes la última
   parada era la sección, el máximo de scroll quedaba por debajo y el recolocado
   devolvía la página arriba: en un portátil de 1.470x830 el pie se quedaba
   cortado 44 px y no había forma de bajar (ENC-20261007-001).

   **La llegada con un ancla la lleva el motor** (ENC-20261014-006). Al abrir la
   portada con `#avisame`, con un ancla vieja o con `#aru-waitlist` —que es como
   vuelve Shopify después de apuntarse: el ancla es el formulario, no la
   sección—, la página va de golpe a la sección, sin el viaje suave desde
   arriba, y se vuelve a colocar al terminar de cargar. Mientras llega, el
   recolocado no cuenta: con el ordenador cargado se paraba a medio viaje y
   dejaba la página en otra sección, o arriba del todo. Quien toca, pulsa o
   desliza mientras tanto manda, también antes de que termine de cargar
   (ENC-20261014-009). Al recargar o al volver atrás no hay llegada: el
   navegador devuelve la página adonde estaba. */
(function () {
  var root = document.documentElement;
  var escritorio = window.matchMedia('(min-width: 901px) and (pointer: fine)');
  var sinMovimiento = window.matchMedia('(prefers-reduced-motion: reduce)');

  var DURACION = 620;   // ms que dura el viaje de un gesto de rueda

  /* Un botón no es un gesto de rueda. La rueda mueve una sección y la
     siguiente ya está ahí; un botón puede cruzar cinco pantallas, y hacerlo en
     los mismos 620 ms sale disparado: se ve un borrón y de golpe otra sección,
     que es lo que Iker describía como brusco. Así que el viaje de los enlaces
     **dura según lo que recorre**: medio segundo largo para lo de al lado, casi
     uno para ir del hero al final de la portada. El tope está para que cruzar
     la portada entera no se haga eterno.

     Con «reducir movimiento» se aplica lo mismo, más corto: la preferencia pide
     menos movimiento, no un teletransporte —eso es justo el tirón del que
     protege— y en el móvil la enciende sola el modo de bajo consumo. */
  var ENLACE_BASE = 420;      // ms fijos de cualquier viaje de enlace
  var ENLACE_POR_PX = 0.1;    // ms más por cada píxel recorrido
  var ENLACE_MIN = 560;
  var ENLACE_MAX = 1150;

  function duracionEnlace(distancia) {
    var d = Math.min(ENLACE_MAX, Math.max(ENLACE_MIN, ENLACE_BASE + distancia * ENLACE_POR_PX));
    return sinMovimiento.matches ? Math.round(d * 0.55) : Math.round(d);
  }
  var REPOSO = 120;     // ms de silencio que separan un gesto del siguiente
  var HOLGURA = 2;      // px por debajo de los cuales ya estamos en el sitio

  var secciones = [];
  var anclas = [];
  var indice = 0;
  var cuadro = null;        // id del requestAnimationFrame en curso
  var suavizadoPrevio = '';
  var ultimaRueda = 0;
  var armado = true;
  var remedirPendiente = false;
  var temporizadorEncaje;
  var llegada = -1;          // sección a la que se llega por el ancla de la dirección, mientras llega
  var anclaLlegada = null;   // el elemento del ancla cuando no es la sección (el formulario)
  var suavizadoLlegada = '';
  var margenLlegada = '';

  function altoCabecera() {
    var cabecera = document.querySelector('.site-header');
    return cabecera ? Math.round(cabecera.getBoundingClientRect().height) : 88;
  }

  function recoger() {
    secciones = Array.prototype.slice.call(document.querySelectorAll('[data-aru-snap]'));
    medir();
  }

  /* La sección a la que lleva un ancla: la de ese `id` o la que lo lleva como
     nombre antiguo (`data-aru-alias`). Los enlaces de la cabecera viven en los
     ajustes del panel, no en el repositorio, y la portada de la versión 1 juntó
     secciones: `#productos` (las familias) es hoy la primera pantalla y
     `#experiencia` («Visítanos») es «Sobre ARU PAPER». Sin esto, esos enlaces
     no llevarían a ningún sitio.
     Si el ancla es algo de dentro de una sección, va a esa sección: Shopify
     devuelve la portada con `#aru-waitlist`, el formulario de «Avísame». */
  function indiceDe(ancla) {
    var i = secciones.findIndex(function (elemento) {
      var alias = elemento.getAttribute('data-aru-alias');
      return '#' + elemento.id === ancla || (!!alias && '#' + alias === ancla);
    });
    if (i !== -1) return i;
    var dentro = elementoDe(ancla);
    if (!dentro) return -1;
    return secciones.findIndex(function (seccion) { return seccion.contains(dentro); });
  }

  function elementoDe(ancla) {
    if (!ancla || ancla.length < 2) return null;
    try { return document.getElementById(decodeURIComponent(ancla.slice(1))); } catch (e) { return null; }
  }

  /* Posición de scroll en la que cada sección queda justo bajo la cabecera.
     La primera va a 0 para no dejar medio píxel de la cinta a la vista.

     Si la última sección y el pie no caben juntos bajo la cabecera, el máximo
     de scroll queda por debajo de su ancla, y se añade ese final como una
     parada más (sin sección: `secciones[i]` no existe para ella). */
  function medir() {
    var cabecera = altoCabecera();
    var tope = Math.max(0, root.scrollHeight - window.innerHeight);
    var desplazamiento = window.scrollY;
    anclas = secciones.map(function (elemento, i) {
      if (i === 0) return 0;
      var arriba = elemento.getBoundingClientRect().top + desplazamiento;
      return Math.min(Math.max(0, Math.round(arriba - cabecera)), tope);
    });
    if (anclas.length && tope - anclas[anclas.length - 1] > HOLGURA) anclas.push(tope);
    if (indice > anclas.length - 1) indice = Math.max(0, anclas.length - 1);
    remedirPendiente = false;
  }

  function pedirMedida() {
    if (remedirPendiente) return;
    remedirPendiente = true;
    requestAnimationFrame(medir);
  }

  function masCercana() {
    var y = window.scrollY;
    return anclas.reduce(function (mejor, valor, i) {
      return Math.abs(valor - y) < Math.abs(anclas[mejor] - y) ? i : mejor;
    }, 0);
  }

  function escribiendo(nodo) {
    if (!nodo || nodo === document.body) return false;
    if (nodo.isContentEditable) return true;
    var etiqueta = nodo.tagName;
    return etiqueta === 'INPUT' || etiqueta === 'TEXTAREA' || etiqueta === 'SELECT';
  }

  /* La barra espaciadora es de un botón cuando el foco está en él: el de pausa
     del vídeo, el del menú, «Quiero apuntarme». Antes el motor se la quedaba,
     la página saltaba a la sección siguiente y el botón no se enteraba
     (ENC-20261014-005, fallo 1). Con el foco en un enlace o en la página, la
     barra sigue siendo «una sección abajo», como en el navegador. */
  function pulsable(nodo) {
    return !!(nodo && nodo.closest && nodo.closest('button, summary, [role="button"], [role="checkbox"], [role="switch"]'));
  }

  /* La rueda se deja al navegador solo encima de lo que se desplaza por dentro.
     Antes se dejaba encima de cualquier campo, casillas incluidas: con el
     puntero sobre «Correo» o un interés, la rueda hacia arriba movía la página
     100 px, el recolocado la devolvía a «Avísame» y no había forma de subir
     (ENC-20261007-002, F5). Un campo de una línea no se desplaza con la rueda. */
  function ruedaPropia(nodo) {
    if (!nodo || !nodo.closest) return false;
    return !!nodo.closest('textarea, select, [contenteditable]:not([contenteditable="false"])');
  }

  function noCabe(elemento) {
    if (!elemento) return false;
    return elemento.getBoundingClientRect().height > window.innerHeight - altoCabecera() + 2;
  }

  function enElBorde(elemento, direccion) {
    var caja = elemento.getBoundingClientRect();
    if (direccion > 0) return caja.bottom <= window.innerHeight + 2;
    return caja.top >= altoCabecera() - 2;
  }

  function detener() {
    window.clearTimeout(temporizadorEncaje);
    if (cuadro === null) return;
    cancelAnimationFrame(cuadro);
    cuadro = null;
    root.style.scrollBehavior = suavizadoPrevio;
  }

  /* Lleva a la sección `destino`. Devuelve true si de verdad hay viaje.
     `porEnlace` marca los viajes que nacen de pulsar algo, que son los que van
     más despacio cuanto más lejos queda la sección. */
  function ir(destino, porEnlace) {
    if (!anclas.length) return false;
    destino = Math.max(0, Math.min(anclas.length - 1, destino));
    var meta = anclas[destino];
    var salida = window.scrollY;
    var anterior = indice;
    indice = destino;
    if (Math.abs(meta - salida) <= HOLGURA) return false;

    detener();
    anunciar(destino, destino > anterior ? 1 : -1);

    var duracion = porEnlace
      ? duracionEnlace(Math.abs(meta - salida))
      : (sinMovimiento.matches ? 260 : DURACION);
    suavizadoPrevio = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    var arranque = 0;

    cuadro = requestAnimationFrame(function paso(ahora) {
      if (!arranque) arranque = ahora;
      var t = Math.min(1, (ahora - arranque) / duracion);
      /* easeInOutCubic: sale y entra despacio, sin rebote. */
      var e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      window.scrollTo(0, Math.round(salida + (meta - salida) * e));
      if (t < 1) {
        cuadro = requestAnimationFrame(paso);
        return;
      }
      cuadro = null;
      root.style.scrollBehavior = suavizadoPrevio;
      window.scrollTo(0, meta);   // cierra cualquier redondeo suelto
    });
    return true;
  }

  function anunciar(destino, direccion) {
    if (!secciones[destino]) return;   // la parada del final de la página
    document.dispatchEvent(new CustomEvent('aru:section-change', {
      detail: { id: secciones[destino].id, index: destino, direction: direccion }
    }));
  }

  function mover(direccion) {
    return ir(indice + direccion);
  }

  function onRueda(evento) {
    terminarLlegada();
    if (!escritorio.matches || evento.ctrlKey) return;
    if (Math.abs(evento.deltaY) <= Math.abs(evento.deltaX)) return;   // gesto horizontal
    if (ruedaPropia(evento.target)) return;
    if (document.body.hasAttribute('data-menu-open')) return;
    if (secciones.length < 2) return;

    var direccion = evento.deltaY > 0 ? 1 : -1;
    var activa = secciones[indice];

    /* Sección más alta que la pantalla: scroll nativo hasta su borde. */
    if (noCabe(activa) && !enElBorde(activa, direccion)) return;

    evento.preventDefault();

    /* La inercia del trackpad sigue disparando eventos después del gesto. Solo
       se acepta el primero de cada tanda: cuenta como tanda nueva cuando ha
       habido un silencio de REPOSO ms. */
    var ahora = performance.now();
    if (ahora - ultimaRueda > REPOSO) armado = true;
    ultimaRueda = ahora;
    if (!armado || cuadro !== null) return;

    armado = false;
    mover(direccion);
  }

  function onTecla(evento) {
    terminarLlegada();
    if (!escritorio.matches || evento.ctrlKey || evento.metaKey || evento.altKey) return;
    if (escribiendo(evento.target)) return;
    if (evento.key === ' ' && pulsable(evento.target)) return;
    if (document.body.hasAttribute('data-menu-open')) return;
    if (secciones.length < 2) return;

    var activa = secciones[indice];
    var flechas = evento.key === 'ArrowDown' || evento.key === 'ArrowUp';
    /* En una sección que no cabe, las flechas siguen sirviendo para leerla. */
    if (flechas && noCabe(activa)) return;

    var movido = false;
    switch (evento.key) {
      case 'PageDown': movido = mover(1); break;
      case 'PageUp': movido = mover(-1); break;
      case 'ArrowDown': movido = mover(1); break;
      case 'ArrowUp': movido = mover(-1); break;
      case 'Home': movido = ir(0); break;
      case 'End': movido = ir(anclas.length - 1); break;
      case ' ': movido = mover(evento.shiftKey ? -1 : 1); break;
      default: return;
    }
    if (movido || evento.key !== ' ') evento.preventDefault();
  }

  /* Los enlaces del menú usan el mismo motor: si no, la cabecera pegajosa
     tapaba el principio de la sección y el encaje no coincidía con el de la
     rueda. */
  function onClic(evento) {
    terminarLlegada();
    if (evento.defaultPrevented || evento.button !== 0) return;
    if (evento.ctrlKey || evento.metaKey || evento.shiftKey) return;
    if (!evento.target || !evento.target.closest) return;
    var enlace = evento.target.closest('a[href*="#"]');
    if (!enlace) return;

    var destino = enlace.hash;
    if (!destino || destino === '#') return;
    if (enlace.pathname !== window.location.pathname || enlace.host !== window.location.host) return;

    var i = indiceDe(destino);
    if (i === -1) return;

    evento.preventDefault();
    if (!ir(i, true)) window.scrollTo(0, anclas[i]);
    history.replaceState(null, '', '#' + secciones[i].id);
    /* Que el teclado y el lector de pantalla vayan también a la sección. */
    var seccion = secciones[i];
    if (!seccion.hasAttribute('tabindex')) seccion.setAttribute('tabindex', '-1');
    seccion.focus({ preventScroll: true });
  }

  function onScroll() {
    if (cuadro !== null) return;   // durante la animación mandamos nosotros
    if (llegada >= 0) return;      // y mientras se llega por un ancla, también
    indice = masCercana();
    reencajarLuego();
  }

  /* La llegada por un ancla (ver arriba). Va de golpe: `scroll-behavior` se
     queda en `auto` mientras dura, así que también el salto del propio
     navegador al ancla. Si el ancla es algo de dentro de la sección, se le da
     el margen justo para que ese salto del navegador caiga donde cae el del
     motor, y no haya dos saltos. */
  function empezarLlegada(i, elemento) {
    llegada = i;
    indice = i;
    suavizadoLlegada = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    if (elemento && elemento !== secciones[i]) {
      anclaLlegada = elemento;
      margenLlegada = elemento.style.scrollMarginTop;
    }
    colocarLlegada();
    requestAnimationFrame(colocarLlegada);
    var asentar = function () {
      requestAnimationFrame(function () {
        colocarLlegada();
        /* Un último repaso por si algo de la carga ha movido la página. */
        window.setTimeout(function () { colocarLlegada(); terminarLlegada(); }, 250);
      });
    };
    if (document.readyState === 'complete') asentar();
    else window.addEventListener('load', asentar);
  }

  /* Dónde se queda: la sección justo bajo la cabecera, como al pulsar un
     enlace. Si el ancla es algo de dentro y lo que tiene que leerse al llegar
     —lo marcado con `data-aru-a-la-vista` dentro del ancla (el «¡Ya estás en
     la lista!» o el error de Shopify) o, si no hay nada marcado, el ancla
     entera— se quedaría por debajo de la pantalla —un móvil pequeño—, se sube
     lo justo para que se vea entero, sin que su principio quede bajo la
     cabecera. */
  function metaDeLlegada() {
    var meta = anclas[llegada];
    var y = window.scrollY;
    if (anclaLlegada) {
      var objetivo = anclaLlegada.querySelector('[data-aru-a-la-vista]') || anclaLlegada;
      var caja = objetivo.getBoundingClientRect();
      var sobra = caja.bottom + y - (meta + window.innerHeight);
      if (sobra > 0) meta = Math.min(meta + sobra, caja.top + y - altoCabecera());
    }
    return Math.round(Math.max(0, Math.min(meta, root.scrollHeight - window.innerHeight)));
  }

  function colocarLlegada() {
    if (llegada < 0 || cuadro !== null) return;
    medir();
    var meta = metaDeLlegada();
    if (anclaLlegada) {
      /* El navegador deja el ancla a `scroll-padding-top` (el alto de la
         cabecera) del borde; con este margen, la deja donde la deja el motor.
         A 1/64 de píxel, que es como coloca el navegador: redondeado al píxel,
         su salto podía quedarse hasta medio píxel fuera. */
      var margen = anclaLlegada.getBoundingClientRect().top + window.scrollY - altoCabecera() - meta;
      anclaLlegada.style.scrollMarginTop = Math.max(0, Math.round(margen * 64) / 64) + 'px';
    }
    /* Medio píxel ya se ve: con la tolerancia de 1 px, si al cargar la letra el
       navegador dejaba la página en 2.146 en vez de 2.147, asomaba una raya
       blanca de la sección de arriba bajo la cabecera (ENC-20261014-008). */
    if (Math.abs(window.scrollY - meta) >= 0.5) window.scrollTo(0, meta);
  }

  /* El margen del ancla se devuelve cuando la página ha terminado de cargar,
     aunque la llegada se acabe antes. Hasta `load` el navegador mantiene la
     página pegada al ancla de la dirección y la vuelve a colocar en cada
     repintado mientras nadie la desplace; quitándole el margen antes, la
     llevaba al sitio de siempre del formulario. En el móvil un toque mientras
     cargaba hacía bajar la página sola hasta él, con el titular de la carta
     cortado, y un deslizamiento de 400 px movía 62 (ENC-20261014-007,
     hallazgo 1). */
  function terminarLlegada() {
    if (llegada < 0) return;
    llegada = -1;
    var ancla = anclaLlegada, margen = margenLlegada;
    anclaLlegada = null;
    root.style.scrollBehavior = suavizadoLlegada;
    if (!ancla) return;
    var devolver = function () { ancla.style.scrollMarginTop = margen; };
    var luego = function () { window.setTimeout(devolver, 300); };
    if (document.readyState === 'complete') luego();
    else window.addEventListener('load', luego);
  }

  /* Hay desplazamientos que no pasan por el motor: arrastrar la barra, un
     AvPág con el foco en un campo del formulario, o el navegador restaurando
     la posición al volver atrás. Cuando el scroll se queda quieto y la sección
     activa cabe en pantalla, se recoloca sola: así nunca se queda a medias. */
  function reencajarLuego() {
    if (!escritorio.matches) return;
    window.clearTimeout(temporizadorEncaje);
    temporizadorEncaje = window.setTimeout(function () {
      if (cuadro !== null || !anclas.length) return;
      if (noCabe(secciones[indice])) return;
      if (Math.abs(window.scrollY - anclas[indice]) <= HOLGURA) return;
      ir(indice);
    }, 220);
  }

  function onResize() {
    pedirMedida();
    if (llegada >= 0) { requestAnimationFrame(colocarLlegada); return; }
    /* En el móvil esto NO se toca. La barra de direcciones del navegador se
       esconde y se enseña sola mientras se hace scroll, y cada vez que lo hace
       cambia `innerHeight` y dispara un `resize`. Con el recolocado puesto,
       leer una sección era imposible: a los dos dedos de scroll el navegador
       daba el aviso de cambio de alto y la página se iba de golpe a la sección
       siguiente. Es el fallo que contaba Iker, y era esta línea. */
    if (!escritorio.matches) return;
    /* En escritorio sí: tras un cambio de tamaño la sección activa se queda a
       medias, y se vuelve a encajar sin animación, que es menos mareante. */
    requestAnimationFrame(function () {
      if (cuadro === null && anclas[indice] !== undefined) window.scrollTo(0, anclas[indice]);
    });
  }

  /* Al recargar o al volver atrás, el navegador devuelve la página adonde la
     tenía la persona, y la llegada no se lo quita: con `#aru-waitlist` en la
     dirección, subir dos secciones y recargar la devolvía a la carta
     (ENC-20261014-007, hallazgo 2). */
  function vuelveASuSitio() {
    try {
      var entrada = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      if (entrada) return entrada.type === 'reload' || entrada.type === 'back_forward';
      var tipo = performance.navigation && performance.navigation.type;
      return tipo === 1 || tipo === 2;
    } catch (e) { return false; }
  }

  recoger();
  var inicial = window.location.hash && !vuelveASuSitio() ? indiceDe(window.location.hash) : -1;
  if (inicial > 0) empezarLlegada(inicial, elementoDe(window.location.hash));
  else indice = masCercana();

  window.addEventListener('wheel', onRueda, { passive: false });
  window.addEventListener('keydown', onTecla);
  document.addEventListener('click', onClic);
  /* Quien toca o pulsa mientras la página llega manda: no se le recoloca. */
  window.addEventListener('touchstart', terminarLlegada, { passive: true, capture: true });
  window.addEventListener('mousedown', terminarLlegada, { capture: true });
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize);
  window.addEventListener('load', pedirMedida);
  document.addEventListener('shopify:section:load', recoger);
  document.addEventListener('shopify:section:unload', recoger);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(pedirMedida);
  if (window.ResizeObserver) new ResizeObserver(pedirMedida).observe(document.body);
})();
